import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useEffect } from 'react';
import { useBackgroundPause } from './useBackgroundPause';
import { useTabLeader } from './useTabLeader';

export interface DashboardAccessNotification {
  id: string;
  code: string;
  dashboard_type: string;
  requested_at: string;
  expires_at: string;
  used_at: string | null;
}

export function useDashboardAccessNotifications() {
  const { user } = useAuth();
  const { isBackground } = useBackgroundPause();
  const { isLeader } = useTabLeader('dashboard-access-leader');

  const { data: notifications = [], refetch } = useQuery({
    queryKey: ['dashboard-access-notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dashboard_access_codes')
        .select('id, code, dashboard_type, requested_at, expires_at, used_at')
        .is('used_at', null)
        .eq('is_active', true)
        .gte('expires_at', new Date().toISOString())
        .order('requested_at', { ascending: false });

      if (error) throw error;
      return (data || []) as DashboardAccessNotification[];
    },
    enabled: !!user,
    // Reduced from 5s to 30s - Realtime handles immediate updates
    // Only poll if this is the leader tab AND tab is visible
    refetchInterval: isLeader && !isBackground ? 30000 : false,
  });

  // Subscribe to real-time changes - only leader tab subscribes
  useEffect(() => {
    if (!user || !isLeader) return;

    const channel = supabase
      .channel('dashboard-access-codes-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'dashboard_access_codes',
        },
        () => {
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'dashboard_access_codes',
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isLeader, refetch]);

  // Refetch when tab becomes visible (after being in background)
  useEffect(() => {
    if (!isBackground && user) {
      refetch();
    }
  }, [isBackground, user, refetch]);

  const unreadCount = notifications.filter(n => !n.used_at).length;

  return {
    notifications,
    unreadCount,
    refetch,
  };
}
